import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  Popover,
  Typography,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FilterOption } from '../../interfaces';
import { UNASSIGNED_RESOURCE_ID } from '../../constants/calendar';

function getInitials(label: string): string {
  const parts = label.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
}

interface Props {
  name: string;
  options: FilterOption[];
  state: string[];
  setState: (value: string[]) => void;
}

export default function ArtistFilterPopover({
  name,
  options,
  state,
  setState,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const open = Boolean(anchorEl);

  const isAllSelected = options.length > 0 && state.length === options.length;

  const commit = (newValue: string[]) => {
    setState(newValue);
    sessionStorage.setItem(name, JSON.stringify(newValue));
  };

  const toggleAll = () =>
    commit(isAllSelected ? [] : options.map(o => o.value));
  const toggleOne = (value: string) =>
    commit(
      state.includes(value)
        ? state.filter(v => v !== value)
        : [...state, value],
    );

  const selectedOptions = React.useMemo(
    () => options.filter(o => state.includes(o.value)),
    [options, state],
  );

  const triggerContent = React.useMemo(() => {
    if (isAllSelected) {
      return { preview: `All staff · ${options.length}`, suffix: null };
    }
    if (state.length === 0) {
      return { preview: 'No staff selected', suffix: null };
    }
    const [firstSelected] = selectedOptions;
    if (!firstSelected) {
      return { preview: `${state.length} selected`, suffix: null };
    }
    if (state.length === 1) {
      return { preview: firstSelected.label, suffix: null };
    }
    return { preview: firstSelected.label, suffix: `+${state.length - 1}` };
  }, [isAllSelected, options.length, state.length, selectedOptions]);

  const content = (
    <Box
      sx={{
        width: isDesktop ? 280 : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={toggleAll}
        onKeyDown={e => e.key === 'Enter' && toggleAll()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: 2,
          py: 1.5,
          bgcolor: 'lightGrey.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
        }}>
        <Checkbox
          checked={isAllSelected}
          size="small"
          sx={{
            p: 0,
            color: 'text.primary',
            '&.Mui-checked': { color: 'common.black' },
          }}
        />
        <Typography variant="h5" sx={{ flex: 1 }}>
          All staff
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {options.length}
        </Typography>
      </Box>
      {options.map(option => {
        const selected = state.includes(option.value);
        const isUnassigned = option.value === UNASSIGNED_RESOURCE_ID;
        return (
          <Box
            key={option.value}
            role="button"
            tabIndex={0}
            onClick={() => toggleOne(option.value)}
            onKeyDown={e => e.key === 'Enter' && toggleOne(option.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              px: 2,
              py: 1,
              cursor: 'pointer',
            }}>
            <Checkbox
              checked={selected}
              size="small"
              sx={{
                p: 0,
                color: option.color || '#333',
                '&.Mui-checked': { color: option.color || '#333' },
              }}
            />
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
                bgcolor: isUnassigned ? 'transparent' : option.color || '#333',
                border: isUnassigned ? '1px dashed' : 'none',
                borderColor: 'lightOutlineGrey.main',
                // eslint-disable-next-line no-restricted-syntax -- typography-exception: badge glyph below the 11px scale floor
                fontSize: '0.625rem',
                fontWeight: 700,
              }}>
              {isUnassigned ? '' : getInitials(option.label)}
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: 'text.primary',
              }}>
              {option.label}
            </Typography>
          </Box>
        );
      })}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'lightGrey.main',
        }}>
        {state.length > 0 && (
          <Button
            onClick={() => commit([])}
            size="small"
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              color: 'primary.main',
              typography: 'caption',
              fontWeight: 600,
            }}>
            Unselect all
          </Button>
        )}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            ml: 'auto',
            fontWeight: 600,
          }}>
          {state.length} of {options.length} showing
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Button
        variant="outlined"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }
        sx={{
          height: 40,
          borderRadius: '5px',
          borderColor: '#000',
          color: 'text.primary',
          textTransform: 'none',
          px: 1.75,
          gap: '8px',
          maxWidth: 220,
          display: 'inline-flex',
          alignItems: 'center',
          typography: 'h5',
        }}>
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '4px',
            overflow: 'hidden',
            minWidth: 0,
          }}>
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}>
            {triggerContent.preview}
          </Box>
          {triggerContent.suffix && (
            <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              {triggerContent.suffix}
            </Box>
          )}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      </Button>
      {isDesktop ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: { sx: { mt: 1, borderRadius: '10px', overflow: 'hidden' } },
          }}>
          {content}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            },
          }}>
          {content}
        </Drawer>
      )}
    </>
  );
}
